import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import {
  AppError,
  Button,
  EmptyState,
  Loader,
  Modal,
} from "@bfc-software/pallet";
import { useOrgCfgFetchClient } from "@bfc-software/bfc-web-ui/lib/orgcfg-api-client";

type DriverQrCode = { userId: string; dataUrl: string };

/**
 * A print-only copy of the grid rendered straight into `document.body`, outside
 * the modal's overlay and portal stack, so `window.print()` has something
 * printable to find: the overlay is `position: fixed` and clipped, which prints
 * blank or cut off. The copy stays hidden except under `@media print`, where the
 * rest of the page is hidden in its place.
 *
 * Hiding every other element on the page reaches siblings outside this
 * component's own subtree, so it is a literal `<style>` tag scoped to this
 * portal rather than a utility class.
 */
function DriverQrPrintArea({ qrCodes }: { qrCodes: DriverQrCode[] }) {
  return createPortal(
    <>
      <style>{`
        @media print {
          body > *:not([data-test-role="driver-qr-print-area"]) {
            display: none !important;
          }
        }
      `}</style>
      <div
        data-test-role="driver-qr-print-area"
        className="hidden p-16 print:block"
      >
        <div className="grid grid-cols-3 gap-16">
          {qrCodes.map((qrCode) => (
            <div
              key={qrCode.userId}
              className="flex flex-col items-center gap-4 break-inside-avoid rounded-8 border border-outline bg-surface p-12"
            >
              {/* A fixed image size, not a layout gap: pallet's spacing scale
                  tops out at 32, so this is an inline `style`. */}
              <img
                className="block"
                // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
                style={{ width: 140, height: 140 }}
                src={qrCode.dataUrl}
                alt=""
              />
              <span className="font-mono text-size-11 font-strong text-on-surface">
                {qrCode.userId}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>,
    document.body,
  );
}

/**
 * Every driver's sign-in QR code, printable.
 *
 * The images are rendered by the config service's `/orgs/user_groups/qr_codes`
 * (the same call Selectors' Team QR Codes uses, with `appName: "deliver"`), so
 * no PIN material reaches the browser, only the finished images. A driver
 * scans theirs once to sign in to the Deliver app.
 */
export function DriverQrCodesModal({
  isOpen,
  onClose,
  orgId,
  warehouseId,
  t,
}: {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  warehouseId: string;
  t: TFunction;
}) {
  const orgCfgClient = useOrgCfgFetchClient();
  const qrQuery = useQuery({
    queryKey: ["driver-qr-codes", orgId, warehouseId],
    queryFn: async () => {
      const { data } = await orgCfgClient.GET("/orgs/user_groups/qr_codes", {
        params: { query: { orgId, warehouseId, appName: "deliver" } },
      });
      if (!data) throw new Error("Failed to load driver QR codes");
      return data.qrCodes;
    },
    enabled: isOpen,
    // Always fresh: a PIN reset since the last open changes the code.
    gcTime: 0,
  });

  const qrCodes = [...(qrQuery.data ?? [])].sort((a, b) =>
    a.userId.localeCompare(b.userId),
  );

  return (
    <>
      {isOpen ? <DriverQrPrintArea qrCodes={qrCodes} /> : null}
      <Modal
        control="external"
        data-test-role="driver-qr-modal"
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        width="wide"
        header={
          <div className="flex items-center gap-12">
            <h2 className="text-size-18 text-on-surface">
              {t("manage.drivers.qr.title")}
            </h2>
            <Button
              kind="text-icon-left"
              variant="secondary"
              scale="compact"
              icon="Printer"
              text={t("manage.drivers.qr.print")}
              isDisabled={qrCodes.length === 0}
              onPress={() => window.print()}
            />
          </div>
        }
      >
        {/* `Modal` never caps a tall body, so the cap is an inline `style`. */}
        <div
          className="flex max-w-full flex-col overflow-y-auto"
          // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
          style={{ maxHeight: "calc(100vh - 320px)" }}
        >
          <p className="mb-16 text-size-13 text-on-surface-variant">
            {t("manage.drivers.qr.intro")}
          </p>

          {qrQuery.isLoading ? (
            <Loader type="page" />
          ) : qrQuery.isError ? (
            <AppError
              description={t("manage.drivers.qr.failed")}
              resolution={t("manage.failureSupport")}
            />
          ) : qrCodes.length === 0 ? (
            <EmptyState heading={t("manage.drivers.qr.empty")} />
          ) : (
            // The cards are a fixed size (the 140px image plus padding), so a
            // wrapping flex row shelves them the way an auto-fill grid would;
            // pallet has no utility for `repeat(auto-fill, minmax(…))`.
            <div className="flex flex-wrap gap-12">
              {qrCodes.map((qrCode) => (
                <div
                  key={qrCode.userId}
                  className="flex flex-col items-center gap-8 rounded-10 border border-outline bg-surface p-16"
                  data-test-role="qr-card"
                >
                  <img
                    className="block"
                    // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
                    style={{ width: 140, height: 140 }}
                    src={qrCode.dataUrl}
                    alt={t("manage.drivers.qr.cardLabel", {
                      userId: qrCode.userId,
                    })}
                  />
                  <span className="font-mono text-size-13 font-strong text-on-surface">
                    {qrCode.userId}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
